import { gsap } from 'gsap';

export { gsap };

export const prefersReducedMotion = () => (
  typeof window !== 'undefined'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

/** Inline props owned by fade/slide tweens (autoAlpha + transform). */
export const GSAP_CLEAR_MOTION = 'opacity,visibility,transform,willChange';

/**
 * Revert GSAP fade/slide inline styles so a killed tween cannot leave
 * visibility:hidden / opacity:0 on the node.
 * @param {Element|Element[]|NodeList|null|undefined} targets
 */
export function clearGsapMotion(targets) {
  if (!targets) return;
  if (typeof targets.length === 'number' && !targets.length) return;
  gsap.set(targets, { clearProps: GSAP_CLEAR_MOTION });
}

/**
 * Kill a tween/timeline and clear motion props on its targets.
 * @param {import('gsap').core.Animation|null|undefined} tween
 * @param {Element|Element[]|NodeList|null|undefined} [targets]
 * @returns {null}
 */
export function killGsapTween(tween, targets) {
  tween?.kill();
  clearGsapMotion(targets);
  return null;
}

/**
 * Shared organogram / shell motion.
 * Soft sine curves, short travel, overlapping staggers — fade-led, not snappy.
 */
export const ORG_MOTION = {
  cameraFit: 0.85,
  cameraPan: 0.65,
  cameraZoom: 0.4,
  cameraEase: 'power2.out',
  flip: 0.55,
  flipEase: 'power1.inOut',
  flipEnter: 0.42,
  flipLeave: 0.28,
  panelIn: 0.55,
  panelOut: 0.36,
  panelEaseIn: 'sine.out',
  panelEaseOut: 'sine.in',
  panelSlideIn: 24,
  panelSlideOut: 16,
  /** Detail panel body swap when another node is picked while open. */
  panelContentIn: 0.32,
  panelContentStagger: 0.04,
  /** Org chart cards that appear after a drill / "View more". */
  nodeIn: 0.5,
  nodeStaggerAmount: 0.24,
  /** Content load-in (lists, stat cards, drill rows). */
  list: 0.75,
  /** Total stagger window across all items (overlapping wave). */
  listStaggerAmount: 0.28,
  listEase: 'sine.out',
  listY: 6,
  breadcrumbIn: 0.36,
  breadcrumbStagger: 0.05,
  searchPanelIn: 0.4,
  searchPanelOut: 0.26,
  countUp: 0.9,
  countUpEase: 'sine.out',
  pageIn: 0.58,
  pageOut: 0.4,
  pageEaseIn: 'sine.out',
  pageEaseOut: 'sine.in',
  pageSlideIn: 8,
  pageSlideOut: 5,
};

/**
 * Events calendar motion — short, fade-led; respects prefers-reduced-motion callers.
 */
export const CALENDAR_MOTION = {
  monthIn: 0.42,
  monthOut: 0.28,
  monthEaseIn: 'sine.out',
  monthEaseOut: 'sine.in',
  monthSlide: 18,
  panelIn: 0.42,
  panelOut: 0.28,
  panelEaseIn: 'sine.out',
  panelEaseOut: 'sine.in',
  panelSlide: 20,
  chipIn: 0.48,
  chipStaggerAmount: 0.32,
  chipEase: 'sine.out',
  chipY: 4,
  list: 0.55,
  listStaggerAmount: 0.22,
  listY: 8,
  listEase: 'sine.out',
};
